/*
 * This file is part of glowingblue/orell-articles.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import search, { getCachedArticleSearch, PAGE_SIZE } from '../search';
import type { ArticleSearchPage, ArticleSearchResult } from '../search';
import type Article from '../models/Article';

export interface ArticleListParams {
	q?: string;
}

export default class ArticleListState {
	params: ArticleListParams;

	loading: boolean = false;

	moreResults: boolean = false;

	/** `false` when the query returned nothing at all. */
	articles: Article[] | false = [];

	/** Whether the articles currently held are the first page, not an appended one. */
	isFirstPage: boolean = true;

	/** `false` when the current page is the last one. */
	nextPage: ArticleSearchPage | false = false;

	constructor(params: ArticleListParams) {
		this.params = params;
	}

	refresh(clear: boolean = true): Promise<void> | undefined {
		const { q } = this.params;

		if (!q) {
			return;
		}

		// When making a search in the dropdown, we already have the results and
		// don't want to make the search request again, so we use the cached data.
		const cached = getCachedArticleSearch();
		if (cached && cached.q === q) {
			const { articles, isFirstPage, nextPage } = cached;
			if (articles !== false && articles.length) {
				this.isFirstPage = isFirstPage;
				this.articles = articles;
				this.nextPage = nextPage;
				this.loading = false;

				m.redraw();

				return;
			}
		}

		if (clear) {
			this.clear();
		}
		this.loading = true;

		return search(q, [], 0, PAGE_SIZE)
			.then((result) => this.handleSearchSuccess(result))
			.then(() => this.manipulateResults())
			.catch((error: unknown) => this.handleSearchError(error));
	}

	loadMore(): void {
		const { nextPage } = this;

		if (nextPage) {
			this.loading = true;
			nextPage()
				.then((result) => this.handleSearchSuccess(result))
				.then(() => this.manipulateResults())
				.catch((error: unknown) => this.handleSearchError(error));
		}
	}

	handleSearchSuccess({ articles, isFirstPage, nextPage }: ArticleSearchResult): void {
		this.isFirstPage = isFirstPage;

		if (isFirstPage || articles === false || this.articles === false) {
			this.articles = articles;
		} else {
			this.articles = [...this.articles, ...articles];
		}

		this.nextPage = nextPage;
		this.loading = false;

		m.redraw();
	}

	/**
	 * Hook for extensions that need to reorder or filter the loaded articles.
	 */
	manipulateResults(): void {}

	/**
	 * The rejection reason is forwarded so that `extend()` consumers of this seam can inspect it,
	 * even though the default implementation does not use it.
	 */
	handleSearchError(_error?: unknown): void {
		this.loading = false;

		m.redraw();
	}

	clear(): void {
		this.loading = false;
		this.articles = [];
		this.nextPage = false;

		m.redraw();
	}
}
